import {
  cleanGhostObjectsFn,
  deleteAllOrphansFn,
  deleteMediaFn,
  listMediaInfiniteOptions,
  uploadMediaFn,
} from "@/actions/media/handler";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { formatBytes } from "@/utils/format";
import { useInfiniteQuery, useMutation } from "@tanstack/react-query";
import { createFileRoute, redirect } from "@tanstack/react-router";
import { Link } from "@tanstack/react-router";
import {
  ArrowLeft,
  ArrowUpDown,
  Copy,
  FileVideo,
  Image as ImageIcon,
  Loader2,
  Play,
  RefreshCw,
  Search,
  Trash2,
  Upload,
} from "lucide-react";
import {
  type ChangeEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { toast } from "sonner";

export const Route = createFileRoute("/_app/admin/media")({
  beforeLoad: async ({ context }) => {
    if (!context.user || context.user.role !== "admin") {
      throw redirect({ to: "/" });
    }
  },
  component: RouteComponent,
});

type MediaType = "all" | "image" | "video" | "orphan" | "unprocessed";
type SortType = "date" | "size" | "name";

function RouteComponent() {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const currentUploadFile = useRef<File | null>(null);
  const [deleteKey, setDeleteKey] = useState<string | null>(null);
  const [filterType, setFilterType] = useState<MediaType>("all");
  const [sortBy, setSortBy] = useState<SortType>("date");
  const [searchQuery, setSearchQuery] = useState("");

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    refetch,
  } = useInfiniteQuery(
    listMediaInfiniteOptions({
      type: filterType,
      search: searchQuery,
      sortBy: sortBy,
    }),
  );

  const objects = useMemo(() => {
    return data?.pages.flatMap((page) => page?.objects || []) || [];
  }, [data]);

  const total = data?.pages[0]?.total || 0;

  const observerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!observerRef.current || !hasNextPage || isFetchingNextPage) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          fetchNextPage();
        }
      },
      { threshold: 0.1 },
    );

    observer.observe(observerRef.current);
    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  // Helper functions
  const isVideo = useCallback(
    (extension: string) =>
      ["mp4", "webm", "mov", "avi"].includes(extension.toLowerCase()),
    [],
  );

  const isImage = useCallback(
    (extension: string) =>
      ["jpg", "jpeg", "png", "gif", "webp"].includes(extension.toLowerCase()),
    [],
  );

  // Calculate statistics
  const stats = useMemo(() => {
    const videoObjects = objects.filter((obj: { extension: string }) =>
      isVideo(obj.extension),
    );
    const imageObjects = objects.filter((obj: { extension: string }) =>
      isImage(obj.extension),
    );
    const orphanObjects = objects.filter(
      (obj: { isOrphan?: boolean }) => obj.isOrphan,
    );
    const unprocessedObjects = objects.filter(
      (obj: { isUnprocessed?: boolean }) => obj.isUnprocessed,
    );

    const videoSize = videoObjects.reduce(
      (sum: number, obj: { size: number }) => sum + obj.size,
      0,
    );
    const imageSize = imageObjects.reduce(
      (sum: number, obj: { size: number }) => sum + obj.size,
      0,
    );
    const orphanSize = orphanObjects.reduce(
      (sum: number, obj: { size: number }) => sum + obj.size,
      0,
    );
    const unprocessedSize = unprocessedObjects.reduce(
      (sum: number, obj: { size: number }) => sum + obj.size,
      0,
    );

    return {
      totalVideos: videoObjects.length,
      totalImages: imageObjects.length,
      totalOrphans: orphanObjects.length,
      totalUnprocessed: unprocessedObjects.length,
      videoSize,
      imageSize,
      orphanSize,
      unprocessedSize,
      totalSize: videoSize + imageSize,
    };
  }, [objects, isVideo, isImage]);

  const uploadMutation = useMutation({
    mutationFn: uploadMediaFn,
    onSettled: () => {
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    },
  });

  const s3UploadMutation = useMutation({
    mutationFn: async ({
      file,
      signedUrl,
    }: {
      file: File;
      signedUrl: string;
    }) => {
      const response = await fetch(signedUrl, {
        method: "PUT",
        body: file,
        headers: {
          "Content-Type": file.type,
        },
      });

      if (!response.ok) {
        throw new Error(
          `S3 upload failed: ${response.status} ${response.statusText}`,
        );
      }

      return response;
    },
    retry: 2,
    onSettled: () => {
      refetch();
    },
    onSuccess: (_, variables) => {
      toast.success("Dosya başarıyla yüklendi!", {
        description: variables.file.name,
        position: "top-center",
      });
    },
    onError: (error: Error) => {
      toast.error("Dosya yüklenemedi!", {
        description: error.message,
        position: "top-center",
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: deleteMediaFn,
    onSettled: () => {
      refetch();
    },
    onSuccess: () => {
      toast.success("Dosya başarıyla silindi!", {
        position: "top-center",
      });
      setDeleteKey(null);
    },
    onError: (error: Error) => {
      toast.error("Silme işlemi başarısız!", {
        description: error.message,
        position: "top-center",
      });
      setDeleteKey(null);
    },
  });

  const cleanGhostsMutation = useMutation({
    mutationFn: cleanGhostObjectsFn,
    onSettled: () => {
      refetch();
    },
    onSuccess: (res) => {
      if (res.status === 200 && res.body) {
        toast.success(
          `${res.body.deletedCount} adet hayalet kayıt temizlendi! (${formatBytes(res.body.freedBytes)} yer açıldı)`,
          { position: "top-center" },
        );
      }
    },
    onError: (error: Error) => {
      toast.error("Temizleme başarısız!", { description: error.message });
    },
  });

  const deleteAllOrphansMutation = useMutation({
    mutationFn: deleteAllOrphansFn,
    onSettled: () => {
      refetch();
    },
    onSuccess: (res) => {
      if (res.status === 200 && res.body) {
        toast.success(
          `${res.body.deletedCount} adet sahipsiz dosya silindi! (${formatBytes(res.body.freedBytes)} yer açıldı)`,
          { position: "top-center" },
        );
      }
    },
    onError: (error: Error) => {
      toast.error("Sahipsiz dosyalar silinemedi!", {
        description: error.message,
      });
    },
  });

  const isUploading = uploadMutation.isPending || s3UploadMutation.isPending;

  const handleFileSelect = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    currentUploadFile.current = file;

    try {
      const response = await uploadMutation.mutateAsync({
        data: {
          fileName: file.name,
          fileSize: file.size,
          contentType: file.type,
        },
      });

      if (response.body?.signedUrl) {
        await s3UploadMutation.mutateAsync({
          file,
          signedUrl: response.body.signedUrl,
        });
      }
    } catch (error) {
      console.error("Upload error:", error);
    } finally {
      currentUploadFile.current = null;
    }
  };

  const handleDeleteConfirm = () => {
    if (deleteKey) {
      deleteMutation.mutate({
        data: {
          key: deleteKey,
        },
      });
    }
  };

  const copyToClipboard = (url: string) => {
    navigator.clipboard.writeText(url);
    toast.success("URL kopyalandı!", {
      position: "top-center",
    });
  };

  return (
    <div className="container mx-auto px-4 py-6 sm:px-6 sm:py-8 lg:px-8">
      {/* Breadcrumb */}
      <div className="mb-6">
        <Button asChild variant="ghost" className="mb-4">
          <Link to="/admin">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Admin Paneli
          </Link>
        </Button>
      </div>
      {/* Header */}
      <div className="mb-6">
        <h1 className="mb-2 font-bold text-2xl sm:text-3xl">Medya Yönetimi</h1>
        <p className="text-muted-foreground text-sm sm:text-base">
          Storage'daki tüm medya dosyalarını yönetin
        </p>
      </div>

      {/* Stats Cards */}
      <div className="mb-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
        <Card>
          <CardContent className="flex items-center gap-4 p-6">
            <div className="rounded-lg bg-primary/10 p-3">
              <ImageIcon className="h-6 w-6 text-primary" />
            </div>
            <div>
              <p className="text-muted-foreground text-sm">Toplam Görsel</p>
              {isLoading ? (
                <Skeleton className="my-1 h-8 w-16" />
              ) : (
                <p className="font-bold text-2xl">{stats.totalImages}</p>
              )}
              {isLoading ? (
                <Skeleton className="h-3 w-16" />
              ) : (
                <p className="text-muted-foreground text-xs">
                  {formatBytes(stats.imageSize)}
                </p>
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="flex items-center gap-4 p-6">
            <div className="rounded-lg bg-primary/10 p-3">
              <FileVideo className="h-6 w-6 text-primary" />
            </div>
            <div>
              <p className="text-muted-foreground text-sm">Toplam Video</p>
              {isLoading ? (
                <Skeleton className="my-1 h-8 w-16" />
              ) : (
                <p className="font-bold text-2xl">{stats.totalVideos}</p>
              )}
              {isLoading ? (
                <Skeleton className="h-3 w-16" />
              ) : (
                <p className="text-muted-foreground text-xs">
                  {formatBytes(stats.videoSize)}
                </p>
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="flex items-center gap-4 p-6">
            <div className="rounded-lg bg-primary/10 p-3">
              <Upload className="h-6 w-6 text-primary" />
            </div>
            <div>
              <p className="text-muted-foreground text-sm">Toplam Dosya</p>
              {isLoading ? (
                <Skeleton className="my-1 h-8 w-16" />
              ) : (
                <p className="font-bold text-2xl">{total}</p>
              )}
              {isLoading ? (
                <Skeleton className="h-3 w-16" />
              ) : (
                <p className="text-muted-foreground text-xs">
                  {
                    new Set(
                      objects.map(
                        (obj: { extension: string }) => obj.extension,
                      ),
                    ).size
                  }{" "}
                  farklı tür
                </p>
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="flex items-center gap-4 p-6">
            <div
              className={`rounded-lg p-3 ${stats.totalOrphans > 0 ? "bg-destructive/10" : "bg-primary/10"}`}
            >
              <Trash2
                className={`h-6 w-6 ${stats.totalOrphans > 0 ? "text-destructive" : "text-primary"}`}
              />
            </div>
            <div>
              <p className="text-muted-foreground text-sm">Sahipsiz</p>
              {isLoading ? (
                <Skeleton className="my-1 h-8 w-16" />
              ) : (
                <p className="font-bold text-2xl">{stats.totalOrphans}</p>
              )}
              {isLoading ? (
                <Skeleton className="h-3 w-16" />
              ) : (
                <p className="text-muted-foreground text-xs">
                  {formatBytes(stats.orphanSize)}
                </p>
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="flex items-center gap-4 p-6">
            <div className="rounded-lg bg-primary/10 p-3">
              <ArrowUpDown className="h-6 w-6 text-primary" />
            </div>
            <div>
              <p className="text-muted-foreground text-sm">Toplam Boyut</p>
              {isLoading ? (
                <Skeleton className="my-1 h-8 w-24" />
              ) : (
                <p className="font-bold text-2xl">
                  {formatBytes(stats.totalSize)}
                </p>
              )}
              <p className="text-muted-foreground text-xs">Kullanım</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Controls: Upload, Filter, Sort */}
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-wrap items-center gap-3">
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*,video/*"
            onChange={handleFileSelect}
            className="hidden"
          />
          <Button
            onClick={() => fileInputRef.current?.click()}
            disabled={isUploading}
            size="lg"
          >
            {isUploading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Yükleniyor...
              </>
            ) : (
              <>
                <Upload className="mr-2 h-4 w-4" />
                Dosya Yükle
              </>
            )}
          </Button>

          <Button
            onClick={() => cleanGhostsMutation.mutate({})}
            disabled={cleanGhostsMutation.isPending}
            variant="outline"
            size="lg"
          >
            {cleanGhostsMutation.isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Temizleniyor...
              </>
            ) : (
              <>
                <RefreshCw className="mr-2 h-4 w-4" />
                Hayalet Verileri Temizle
              </>
            )}
          </Button>

          {stats.totalOrphans > 0 && (
            <Button
              onClick={() => deleteAllOrphansMutation.mutate({})}
              disabled={deleteAllOrphansMutation.isPending}
              variant="destructive"
              size="lg"
            >
              {deleteAllOrphansMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Siliniyor...
                </>
              ) : (
                <>
                  <Trash2 className="mr-2 h-4 w-4" />
                  Sahipsiz Dosyaları Sil ({stats.totalOrphans})
                </>
              )}
            </Button>
          )}
        </div>

        <div className="flex flex-col gap-3 sm:flex-row">
          <div className="relative flex-1">
            <Search className="-translate-y-1/2 absolute top-1/2 left-3 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Dosya adıyla ara..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
            />
          </div>

          <Select
            value={filterType}
            onValueChange={(value: MediaType) => setFilterType(value)}
          >
            <SelectTrigger className="w-full sm:w-[180px]">
              <SelectValue placeholder="Filtrele" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tümü</SelectItem>
              <SelectItem value="image">Görseller</SelectItem>
              <SelectItem value="video">Videolar</SelectItem>
              <SelectItem value="orphan">
                Sahipsiz Dosyalar
                {stats.totalOrphans > 0 && ` (${stats.totalOrphans})`}
              </SelectItem>
              <SelectItem value="unprocessed">
                İşlenmemiş Videolar
                {stats.totalUnprocessed > 0 && ` (${stats.totalUnprocessed})`}
              </SelectItem>
            </SelectContent>
          </Select>

          <Select
            value={sortBy}
            onValueChange={(value: SortType) => setSortBy(value)}
          >
            <SelectTrigger className="w-full sm:w-[180px]">
              <SelectValue placeholder="Sırala" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="date">Tarihe Göre</SelectItem>
              <SelectItem value="size">Boyuta Göre</SelectItem>
              <SelectItem value="name">İsme Göre</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Upload Progress Card */}
      {isUploading && currentUploadFile.current && (
        <Card className="mb-6">
          <CardContent className="flex items-center gap-4 py-4">
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
            <div className="flex-1">
              <p className="font-medium text-sm">
                {uploadMutation.isPending ? "Hazırlanıyor..." : "Yükleniyor..."}
              </p>
              <p className="text-muted-foreground text-xs">
                {currentUploadFile.current.name} •{" "}
                {formatBytes(currentUploadFile.current.size)}
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="mb-4">
        {isLoading ? (
          <Skeleton className="h-4 w-48" />
        ) : (
          <p className="text-muted-foreground text-sm">
            {total} dosyadan {objects.length} tanesi yüklendi
            {filterType !== "all" &&
              ` (${
                filterType === "video"
                  ? "video"
                  : filterType === "image"
                    ? "görsel"
                    : filterType === "orphan"
                      ? "sahipsiz dosya"
                      : "işlenmemiş video"
              })`}
          </p>
        )}
      </div>

      {isLoading ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {Array.from({ length: 8 }).map((_, i) => (
            <Card key={i} className="overflow-hidden">
              <Skeleton className="aspect-square w-full" />
              <CardContent className="space-y-2 p-4">
                <Skeleton className="h-4 w-3/4" />
                <Skeleton className="h-3 w-1/2" />
                <div className="flex gap-2 pt-2">
                  <Skeleton className="h-9 flex-1" />
                  <Skeleton className="h-9 w-9" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : objects.length === 0 && !isFetchingNextPage ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12 sm:py-16">
            <ImageIcon className="mb-4 h-12 w-12 text-muted-foreground sm:h-16 sm:w-16" />
            <h3 className="mb-2 font-semibold text-base sm:text-lg">
              {filterType === "all"
                ? "Medya bulunamadı"
                : filterType === "video"
                  ? "Video bulunamadı"
                  : filterType === "image"
                    ? "Görsel bulunamadı"
                    : filterType === "orphan"
                      ? "Sahipsiz dosya yok"
                      : "İşlenmemiş video yok"}
            </h3>
            <p className="text-muted-foreground text-xs sm:text-sm">
              {filterType === "all"
                ? "Henüz yüklenmiş dosya yok"
                : filterType === "orphan"
                  ? "Tüm dosyalar bir workout tarafından kullanılıyor ✅"
                  : filterType === "unprocessed"
                    ? "Tüm videolar başarıyla işlenmiş ✅"
                    : `Henüz yüklenmiş ${filterType === "video" ? "video" : "görsel"} yok`}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-6">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {objects.map(
              (object: {
                fullKey: string;
                extension: string;
                url: string;
                key: string;
                size: number;
                isOrphan?: boolean;
                isUnprocessed?: boolean;
              }) => {
                const isVideoFile = isVideo(object.extension);
                const isImageFile = isImage(object.extension);

                return (
                  <Card key={object.fullKey} className="overflow-hidden">
                    <Dialog>
                      <DialogTrigger asChild>
                        <div className="group relative aspect-square cursor-pointer overflow-hidden bg-muted">
                          {isImageFile ? (
                            <img
                              src={object.url}
                              alt={object.key}
                              className="h-full w-full object-cover transition-transform group-hover:scale-105"
                              loading="lazy"
                            />
                          ) : isVideoFile ? (
                            <div className="flex h-full w-full items-center justify-center bg-muted">
                              <FileVideo className="h-12 w-12 text-muted-foreground" />
                              <div className="absolute inset-0 flex items-center justify-center bg-black/20 opacity-0 transition-opacity group-hover:opacity-100">
                                <Play className="h-10 w-10 fill-white text-white" />
                              </div>
                            </div>
                          ) : (
                            <div className="flex h-full w-full items-center justify-center">
                              <ImageIcon className="h-12 w-12 text-muted-foreground" />
                            </div>
                          )}
                        </div>
                      </DialogTrigger>

                      <DialogContent className="max-w-4xl">
                        <div className="space-y-4">
                          <h3 className="font-semibold text-lg">
                            {object.key}
                          </h3>
                          <div className="flex aspect-video w-full items-center justify-center overflow-hidden rounded-lg bg-black">
                            {isVideoFile ? (
                              <video
                                src={object.url}
                                controls
                                className="max-h-[70vh] w-full"
                              >
                                <track kind="captions" />
                              </video>
                            ) : (
                              <img
                                src={object.url}
                                alt={object.key}
                                className="max-h-[70vh] object-contain"
                              />
                            )}
                          </div>
                          <div className="flex items-center justify-between text-muted-foreground text-sm">
                            <span>Boyut: {formatBytes(object.size)}</span>
                            <span>Tür: {object.extension.toUpperCase()}</span>
                          </div>
                        </div>
                      </DialogContent>
                    </Dialog>

                    <CardContent className="p-4">
                      <div className="mb-2 flex items-start justify-between gap-2">
                        <p className="truncate font-medium text-sm">
                          {object.key}
                        </p>
                      </div>

                      <div className="mb-3 flex items-center gap-2 text-muted-foreground text-xs">
                        <span className="uppercase">{object.extension}</span>
                        <span>•</span>
                        <span>{formatBytes(object.size)}</span>
                      </div>

                      <div className="flex gap-2">
                        <button
                          onClick={() => copyToClipboard(object.url)}
                          className="flex flex-1 items-center justify-center gap-2 rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                          type="button"
                        >
                          <Copy className="h-4 w-4" />
                          Kopyala
                        </button>
                        <button
                          onClick={() => setDeleteKey(object.fullKey)}
                          disabled={deleteMutation.isPending}
                          className="flex items-center justify-center rounded-md border border-destructive bg-background px-3 py-2 text-destructive text-sm ring-offset-background transition-colors hover:bg-destructive hover:text-destructive-foreground focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50"
                          type="button"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </CardContent>
                  </Card>
                );
              },
            )}
          </div>

          {hasNextPage && (
            <div
              ref={observerRef}
              className="flex flex-col items-center justify-center gap-2 py-8"
            >
              <Loader2 className="h-6 w-6 animate-spin text-primary" />
              <span className="text-muted-foreground text-xs">
                Daha fazla medya yükleniyor... ({total - objects.length} dosya
                kaldı)
              </span>
            </div>
          )}
        </div>
      )}
      <AlertDialog open={!!deleteKey} onOpenChange={() => setDeleteKey(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Dosyayı silmek istediğinize emin misiniz?
            </AlertDialogTitle>
            <AlertDialogDescription>
              Bu işlem geri alınamaz. Dosya kalıcı olarak silinecektir.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteMutation.isPending}>
              İptal
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              disabled={deleteMutation.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteMutation.isPending ? "Siliniyor..." : "Sil"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
